import type { OidcClientSettings, UserManagerSettings } from 'oidc-client-ts';
import { UserManager, User } from 'oidc-client-ts';

export const redirectUri = `${window.location.origin}/auth/callback`;

// Azure AD B2C configuration from environment variables (REQUIRED)
const b2cTenant = import.meta.env.VITE_B2C_TENANT;
const b2cPolicy = import.meta.env.VITE_B2C_POLICY;
const b2cApiScope = import.meta.env.VITE_B2C_API_SCOPE;
const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID;

// Validate required environment variables at build time
if (!b2cTenant || !b2cPolicy || !b2cApiScope || !clientId) {
  throw new Error(
    'Missing required OAuth environment variables. ' +
      'Please ensure VITE_B2C_TENANT, VITE_B2C_POLICY, VITE_B2C_API_SCOPE, ' +
      'and VITE_OAUTH_CLIENT_ID are set in your .env file or CI/CD variables.'
  );
}

export { clientId };

// Azure AD B2C authority URL format: https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/{policy}/v2.0
export const authority = `https://${b2cTenant}.b2clogin.com/${b2cTenant}.onmicrosoft.com/${b2cPolicy}/v2.0`;

/** Base URL for Azure B2C (authority without /v2.0) for building authorize/logout endpoints */
const authorityBase = authority.replace(/\/v2.0$/, '');

/**
 * Returns the Azure AD B2C authorize (sign-in) URL. Used as post_logout_redirect_uri
 * so that after logout the user is redirected to the Azure login page.
 */
export function getAzureLoginUrl(): string {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: `openid ${b2cApiScope}`,
  });
  return `${authorityBase}/oauth2/v2.0/authorize?${params.toString()}`;
}

export const authConfig: OidcClientSettings = {
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  response_type: 'code',
  scope: `openid ${b2cApiScope}`,
  loadUserInfo: false,
};

class AuthService {
  private userManager: UserManager;

  constructor() {
    const settings: UserManagerSettings = {
      ...authConfig,
      post_logout_redirect_uri: window.location.origin,
      silent_redirect_uri: `${window.location.origin}/silent-callback`,
    };

    this.userManager = new UserManager(settings);
  }

  async getToken(): Promise<string | null> {
    try {
      const user = await this.userManager.getUser();
      if (!user || user.expired) {
        return null;
      }
      return user.access_token;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error getting token:', error);
      return null;
    }
  }

  async login(): Promise<void> {
    await this.userManager.signinRedirect();
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.userManager.getUser();
      return user !== null && !user.expired;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  async logout(): Promise<void> {
    await this.userManager.signoutRedirect();
  }

  // Handle the callback after login
  async handleCallback(): Promise<User | null> {
    try {
      return await this.userManager.signinRedirectCallback();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error handling callback:', error);
      return null;
    }
  }
}

export const authService = new AuthService();
